"use client";

import type { FooterSocial } from "../../../types";
import { RuntimeLink } from "../../../runtime/RendererRuntime";
import { useDevPlaceholder } from "../../../runtime/DevPlaceholder";
import { isExternalHref, resolveConfiguredHref } from "../../../utils/links";
import { isKnownSocialIcon, SocialBrandIcon } from "./SocialBrandIcon";

/**
 * Footer social row — each network as its own brand-coloured disc with a white
 * glyph: Instagram's signature gradient, Facebook blue, TikTok black, YouTube
 * red, etc. Discs lift on hover; near-black brands get a hairline ring so they
 * separate from the dark footer. Replaces the old uniform amber discs.
 *
 * The outer flex container accepts a {@link className} so callers can adjust
 * alignment and spacing per viewport (centered on mobile, left-aligned on
 * desktop).
 */
const BRAND_BG: Record<string, string> = {
	facebook: "#1877F2",
	instagram: "linear-gradient(45deg,#FEDA75 5%,#FA7E1E 25%,#D62976 55%,#962FBF 80%,#4F5BD5 100%)",
	tiktok: "#111014",
	youtube: "#FF0000",
	linkedin: "#0A66C2",
	twitter: "#0F0F0F",
	whatsapp: "#25D366",
	// Contact affordances that sometimes ride in the same row keep the brand green.
	mail: "#00B862",
	phone: "#00B862",
};

// Near-black brands need a hairline so the disc reads against the dark footer.
const RINGED = new Set(["tiktok", "twitter"]);

/** Host fragments → icon, for entries whose payload names no (known) icon. */
const HOST_ICONS: [RegExp, string][] = [
	[/wa\.me|whatsapp\./i, "whatsapp"],
	[/instagram\./i, "instagram"],
	[/tiktok\./i, "tiktok"],
	[/facebook\.|fb\.com/i, "facebook"],
	[/youtube\.|youtu\.be/i, "youtube"],
	[/linkedin\./i, "linkedin"],
	[/twitter\.|(^|\/\/)x\.com/i, "twitter"],
	[/^mailto:/i, "mail"],
	[/^tel:/i, "phone"],
];

/**
 * Resolves the icon for a social entry. Accepts the canonical `icon` field, the
 * legacy `platform` alias used by older production siteSettings payloads, and
 * finally infers the network from the href host. Returns null when nothing
 * matches so callers can skip the entry instead of rendering an empty disc.
 */
function resolveSocialIcon(social: FooterSocial): string | null {
	for (const candidate of [social.icon, social.platform]) {
		const key = candidate?.toString().trim().toLowerCase();
		if (isKnownSocialIcon(key)) return key;
	}
	const href = resolveConfiguredHref(social.href) ?? "";
	for (const [pattern, icon] of HOST_ICONS) {
		if (pattern.test(href)) return icon;
	}
	return null;
}

export function SocialDiscRow({ items, className }: { items: FooterSocial[]; className?: string }) {
	const { previewMode } = useDevPlaceholder();
	// Production hides social icons with no configured destination; preview keeps them.
	// Entries whose network can't be resolved are always skipped — an empty disc
	// with no glyph communicates nothing.
	const visible = (items ?? [])
		.map((social) => ({ social, icon: resolveSocialIcon(social) }))
		.filter((entry): entry is { social: FooterSocial; icon: string } => Boolean(entry.icon))
		.filter(({ social }) => previewMode || resolveConfiguredHref(social.href));
	if (!visible.length) return null;
	const wrapperClass = ["flex flex-wrap items-center gap-2.5", className].filter(Boolean).join(" ");
	return (
		<div className={wrapperClass}>
			{visible.map(({ social, icon }) => {
				const external = isExternalHref(social.href);
				const targetProps = external
					? ({ target: "_blank", rel: icon === "mail" ? undefined : "noreferrer" } as const)
					: ({} as const);
				const ring = RINGED.has(icon) ? "ring-1 ring-white/15" : "";
				const label = social.label || `APA Parts — ${icon}`;
				return (
					<RuntimeLink
						key={`${icon}-${label}`}
						href={social.href}
						aria-label={label}
						title={label}
						className={`apa-footer-social grid h-10 w-10 place-items-center rounded-full text-white shadow-[var(--shadow-e1)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-e2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${ring}`}
						style={{ background: BRAND_BG[icon] || "#00B862" }}
						{...targetProps}
					>
						<SocialBrandIcon type={icon} />
					</RuntimeLink>
				);
			})}
		</div>
	);
}
